import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import TextInput from '../../../components/ui/TextInput'
import FormDialog from '../../../components/ui/FormDialog'
import FormField from '../../../components/ui/FormField'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import {
  NAME_MAX_LEN,
  operationCodeFormSchema,
  type OperationCodeFormValues,
} from './operationCodeFormSchema'
import type { OperationCode } from '../types'

interface Props {
  visible: boolean
  initial: OperationCode | null
  submitting: boolean
  onSubmit: (nameTr: string, nameEn: string | null) => void
  onHide: () => void
}

/**
 * İşlem Kodu ad düzenleme penceresi (045) — **yalnız TR/EN ad** düzenlenir.
 *
 * <p>Modül ve durum bilgisi gösterilir ama **salt-okumadır** (`disabled` input): kayıt kümesi ve modül
 * ataması veritabanı tarafında yönetilir (FR-008/FR-009). İngilizce ad **boşaltılabilir** — boş gönderim
 * `null`'a çevrilir ve geçerli bir güncellemedir (FR-010).
 */
export default function OperationCodeFormDialog({
  visible,
  initial,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(operationCodeFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible) reset({ nameTr: initial?.nameTr ?? '', nameEn: initial?.nameEn ?? '' })
  }, [visible, initial, reset])

  const onValid = (v: OperationCodeFormValues) => onSubmit(v.nameTr, v.nameEn ? v.nameEn : null)

  const statusLabel =
    initial?.recordStatus === 1
      ? t('admin.operationCodes.status.active')
      : t('admin.operationCodes.status.passive')

  return (
    <FormDialog
      visible={visible}
      header={t('admin.operationCodes.form.editTitle')}
      submitLabel={t('admin.operationCodes.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        {/*
          Salt-okuma bağlam alanları: bu ekrandan değiştirilemez (FR-009). Paylaşılan `FormField`
          kullanılır (Reuse Mandate) — böylece label `htmlFor` ile kontrole bağlanır ve alan ekran
          okuyucu/otomatik testlerde erişilebilir olur.
        */}
        <FormField label={t('admin.operationCodes.form.module')} htmlFor="operationCodeModule">
          <TextInput id="operationCodeModule" value={initial?.moduleName ?? ''} disabled />
        </FormField>
        <FormField label={t('admin.operationCodes.form.status')} htmlFor="operationCodeStatus">
          <TextInput id="operationCodeStatus" value={initial ? statusLabel : ''} disabled />
        </FormField>
        <FormTextField
          control={control}
          name="nameTr"
          label={t('admin.operationCodes.form.nameTr')}
          required
          maxLength={NAME_MAX_LEN}
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('admin.operationCodes.form.nameEn')}
          maxLength={NAME_MAX_LEN}
        />
      </div>
    </FormDialog>
  )
}
